import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShellSkin, useAppShell } from '../../contexts/AppShellContext';
import PlanComplianceBanner from '../Billing/PlanComplianceBanner';
import { useAuth } from '../../contexts/AuthContext';

export const SITE_TITLE = 'Kitchen — Automation Recipes Anyone Can Write and Use';

const SKINS: Array<{ value: AppShellSkin; label: string; description: string }> = [
  { value: 'ide', label: 'IDE', description: 'The complete workspace' },
  { value: 'yolo', label: 'Yolo', description: 'Focus on the recipe' },
];

interface SiteShellProps {
  children: React.ReactNode;
  workspace?: boolean;
}

const SiteShell: React.FC<SiteShellProps> = ({ children, workspace = false }) => {
  const { skin, setSkin } = useAppShell();
  const { impersonation, exitImpersonation } = useAuth();
  const [skinMenuOpen, setSkinMenuOpen] = useState(false);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const skinMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!skinMenuOpen) return;
    const closeMenu = (event: MouseEvent) => {
      if (!skinMenuRef.current?.contains(event.target as Node)) setSkinMenuOpen(false);
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [skinMenuOpen]);

  return (
    <div className="flex h-screen h-[100dvh] w-full flex-col overflow-hidden bg-gray-50">
      {impersonation && <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-amber-300 bg-amber-100 px-3 py-2 text-sm text-amber-950 sm:px-5">
        <div>
          <span className="font-semibold">You are logged in as {impersonation.target.email}</span>
          <span className="ml-2 hidden text-amber-800 sm:inline">Session expires at {new Date(impersonation.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.</span>
        </div>
        <button
          type="button"
          disabled={endingImpersonation}
          onClick={() => {
            setEndingImpersonation(true);
            void exitImpersonation().finally(() => setEndingImpersonation(false));
          }}
          className="rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 shadow-sm hover:bg-amber-50 disabled:opacity-50"
        >
          {endingImpersonation ? 'Exiting…' : 'Exit user account'}
        </button>
      </div>}
      <PlanComplianceBanner />
      {!workspace && skin !== 'yolo' && <header className="hidden h-14 shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-5 lg:flex">
        <Link
          to="/"
          className="min-w-0 truncate text-base font-semibold tracking-tight text-gray-950 hover:text-gray-700 sm:text-lg"
          title="Go to home page"
        >
          {SITE_TITLE}
        </Link>

        <div className="relative shrink-0" ref={skinMenuRef}>
          <button
            type="button"
            onClick={() => setSkinMenuOpen(open => !open)}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:px-3"
            aria-label={`Change app view. Current view: ${skin === 'ide' ? 'IDE' : 'Yolo'}`}
            aria-haspopup="menu"
            aria-expanded={skinMenuOpen}
          >
            <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="2.75" y="3.25" width="14.5" height="13.5" rx="2" />
              <path d="M7.25 3.5v13M7.5 7.25h9.25" />
            </svg>
            <span>{skin === 'ide' ? 'IDE' : 'Yolo'}</span>
            <svg className={`h-3.5 w-3.5 text-gray-400 transition-transform ${skinMenuOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
            </svg>
          </button>

          {skinMenuOpen && (
            <div className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl" role="menu" aria-label="Choose app view">
              {SKINS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={skin === option.value}
                  onClick={() => {
                    setSkin(option.value);
                    setSkinMenuOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${skin === option.value ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${skin === option.value ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'}`}>
                    {skin === option.value && (
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <path d="m5 10 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900">{option.label}</span>
                    <span className="block text-xs text-gray-500">{option.description}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>}
      <div className="min-h-0 flex-1 overflow-hidden">
        {children}
      </div>
    </div>
  );
};

export default SiteShell;
